import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Tag, Button, Modal, Select, Input, DatePicker, Space,
    Tooltip, Spin, Alert, message, Checkbox,
} from 'antd';
import {
    PhoneOutlined, RobotOutlined, MessageOutlined, ReloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import adminService from '../../services/adminService';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const STATUS_META = {
    PENDING: { label: 'Aranacak', className: 'admin-tag-warning' },
    CALLED: { label: 'Arandı', className: 'admin-tag-success' },
    UNREACHABLE: { label: 'Ulaşılamadı', className: 'admin-tag-primary' },
    NOT_INTERESTED: { label: 'İlgilenmiyor', className: 'admin-tag-danger' },
};

const STATUS_FILTERS = [
    { key: null, label: 'Tümü', statKey: 'total' },
    { key: 'PENDING', label: 'Aranacak', statKey: 'pending' },
    { key: 'CALLED', label: 'Arandı', statKey: 'called' },
    { key: 'UNREACHABLE', label: 'Ulaşılamadı', statKey: 'unreachable' },
    { key: 'NOT_INTERESTED', label: 'İlgilenmiyor', statKey: 'notInterested' },
];

const LeadsPage = () => {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState(null);
    const [unansweredOnly, setUnansweredOnly] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);

    // Ozet modal
    const [summaryLead, setSummaryLead] = useState(null);
    const [summaryText, setSummaryText] = useState('');
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState(null);

    // Ham konusma modal
    const [transcriptLead, setTranscriptLead] = useState(null);
    const [transcript, setTranscript] = useState([]);
    const [transcriptLoading, setTranscriptLoading] = useState(false);

    // Durum modal
    const [statusLead, setStatusLead] = useState(null);
    const [statusValue, setStatusValue] = useState('CALLED');
    const [noteValue, setNoteValue] = useState('');
    const [statusSaving, setStatusSaving] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const startDate = dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : null;
            const endDate = dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : null;
            const [listData, statsData] = await Promise.all([
                adminService.getLeads(statusFilter, search, startDate, endDate, unansweredOnly, currentPage, pageSize),
                adminService.getLeadStats(),
            ]);
            setLeads(listData.content || []);
            setTotal(listData.totalElements || 0);
            setStats(statsData || null);
        } catch (error) {
            message.error('Kayıtlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, search, dateRange, unansweredOnly, currentPage, pageSize]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openSummary = async (lead) => {
        setSummaryLead(lead);
        setSummaryText('');
        setSummaryError(null);
        await loadSummary(lead, false);
    };

    const loadSummary = async (lead, refresh) => {
        setSummaryLoading(true);
        setSummaryError(null);
        try {
            const data = await adminService.generateLeadSummary(lead.id, refresh);
            setSummaryText(data.summary || '');
            if (refresh) fetchData();
        } catch (error) {
            setSummaryError('Özet üretilemedi. Ham konuşmayı görüntüleyebilirsiniz.');
        } finally {
            setSummaryLoading(false);
        }
    };

    const openTranscript = async (lead) => {
        setTranscriptLead(lead);
        setTranscript([]);
        setTranscriptLoading(true);
        try {
            const data = await adminService.getLeadMessages(lead.id);
            setTranscript(data || []);
        } catch (error) {
            message.error('Konuşma yüklenemedi');
        } finally {
            setTranscriptLoading(false);
        }
    };

    const openStatus = (lead) => {
        setStatusLead(lead);
        setStatusValue(lead.callStatus === 'PENDING' ? 'CALLED' : lead.callStatus);
        setNoteValue(lead.callNote || '');
    };

    const saveStatus = async () => {
        setStatusSaving(true);
        try {
            await adminService.updateLeadStatus(statusLead.id, statusValue, noteValue);
            message.success('Durum güncellendi');
            setStatusLead(null);
            fetchData();
        } catch (error) {
            message.error('Durum güncellenemedi');
        } finally {
            setStatusSaving(false);
        }
    };

    const formatDateTime = (value) =>
        value ? new Date(value).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-';

    const columns = [
        {
            title: 'Ad Soyad',
            key: 'name',
            render: (_, r) => `${r.customerName} ${r.customerSurname}`,
        },
        {
            title: 'Telefon',
            dataIndex: 'customerPhone',
            key: 'customerPhone',
            render: (phone) => <a href={`tel:${phone}`}>{phone}</a>,
        },
        {
            title: 'Konuşma',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (value) => formatDateTime(value),
        },
        {
            title: 'Mesaj',
            dataIndex: 'messageCount',
            key: 'messageCount',
            width: 90,
            render: (count, r) => (
                <Space size={4}>
                    <span>{count}</span>
                    {r.unansweredCount > 0 && (
                        <Tooltip title={`${r.unansweredCount} soru cevaplanamadı`}>
                            <Tag className="admin-tag admin-tag-danger">!</Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Durum',
            dataIndex: 'callStatus',
            key: 'callStatus',
            width: 130,
            render: (status) => {
                const meta = STATUS_META[status] || { label: status, className: '' };
                return <Tag className={`admin-tag ${meta.className}`}>{meta.label}</Tag>;
            },
        },
        {
            title: 'Son İşlem',
            key: 'lastAction',
            render: (_, r) =>
                r.calledAt ? (
                    <Tooltip title={r.callNote || 'Not girilmemiş'}>
                        <span>{r.calledByDisplayName || '-'}<br />{formatDateTime(r.calledAt)}</span>
                    </Tooltip>
                ) : '-',
        },
        {
            title: '',
            key: 'actions',
            fixed: 'right',
            width: 160,
            render: (_, r) => (
                <Space size={4}>
                    <Tooltip title="AI Özeti">
                        <Button className="admin-btn-icon" icon={<RobotOutlined />} onClick={() => openSummary(r)} />
                    </Tooltip>
                    <Tooltip title="Ham Konuşma">
                        <Button className="admin-btn-icon" icon={<MessageOutlined />} onClick={() => openTranscript(r)} />
                    </Tooltip>
                    <Tooltip title="Durumu Güncelle">
                        <Button className="admin-btn-icon" icon={<PhoneOutlined />} onClick={() => openStatus(r)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <div className="admin-page-header">
                <h2 className="admin-page-title">Aranacaklar</h2>
                <p className="admin-page-subtitle">
                    Chatbot ile konuşan kişiler. Toplam {total} kayıt.
                </p>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                {STATUS_FILTERS.map((f) => (
                    <Button
                        key={f.key || 'all'}
                        className={statusFilter === f.key ? 'admin-btn-primary' : 'admin-btn-secondary'}
                        onClick={() => { setStatusFilter(f.key); setCurrentPage(0); }}
                    >
                        {f.label}{stats ? ` (${stats[f.statKey] ?? 0})` : ''}
                    </Button>
                ))}

                <Checkbox
                    checked={unansweredOnly}
                    onChange={(e) => { setUnansweredOnly(e.target.checked); setCurrentPage(0); }}
                >
                    Sadece bot cevaplayamadı
                </Checkbox>

                <RangePicker
                    onChange={(range) => { setDateRange(range); setCurrentPage(0); }}
                    format="YYYY-MM-DD"
                />

                <Input
                    placeholder="Ad, soyad veya telefon"
                    prefix={<SearchOutlined />}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onPressEnter={() => { setSearch(searchInput.trim()); setCurrentPage(0); }}
                    style={{ minWidth: 180, flex: '1 1 180px', maxWidth: 320 }}
                />
            </div>

            <div className="admin-table-container">
                <Table
                    scroll={{ x: 'max-content' }}
                    rowKey="id"
                    columns={columns}
                    dataSource={leads}
                    loading={loading}
                    pagination={{
                        current: currentPage + 1,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        onChange: (page, size) => {
                            if (size !== pageSize) {
                                setPageSize(size);
                                setCurrentPage(0);
                            } else {
                                setCurrentPage(page - 1);
                            }
                        },
                    }}
                />
            </div>

            {/* AI Ozet */}
            <Modal
                open={!!summaryLead}
                onCancel={() => setSummaryLead(null)}
                className="admin-modal"
                rootClassName="admin-modal"
                title={summaryLead ? `AI Özeti — ${summaryLead.customerName} ${summaryLead.customerSurname}` : ''}
                footer={[
                    <Button
                        key="refresh"
                        className="admin-btn-secondary"
                        icon={<ReloadOutlined />}
                        loading={summaryLoading}
                        onClick={() => loadSummary(summaryLead, true)}
                    >
                        Özeti Yenile
                    </Button>,
                    <Button key="close" className="admin-btn-primary" onClick={() => setSummaryLead(null)}>
                        Kapat
                    </Button>,
                ]}
            >
                {summaryLoading ? (
                    <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
                ) : summaryError ? (
                    <Alert type="warning" showIcon message={summaryError} />
                ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{summaryText}</div>
                )}
            </Modal>

            {/* Ham konusma */}
            <Modal
                open={!!transcriptLead}
                onCancel={() => setTranscriptLead(null)}
                className="admin-modal"
                rootClassName="admin-modal"
                width="92vw"
                style={{ maxWidth: 720 }}
                title={transcriptLead ? `Konuşma — ${transcriptLead.customerName} ${transcriptLead.customerSurname}` : ''}
                footer={[
                    <Button key="close" className="admin-btn-primary" onClick={() => setTranscriptLead(null)}>
                        Kapat
                    </Button>,
                ]}
            >
                {transcriptLoading ? (
                    <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
                ) : transcript.length === 0 ? (
                    <Alert type="info" showIcon message="Bu oturumda mesaj yok." />
                ) : (
                    <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                        {transcript.map((m, i) => (
                            <div key={i} style={{ marginBottom: 12 }}>
                                <strong>{m.senderType === 'CUSTOMER' ? 'Müşteri' : 'Bot'}</strong>
                                <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.6 }}>
                                    {formatDateTime(m.sentAt)}
                                </span>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal>

            {/* Durum guncelleme */}
            <Modal
                open={!!statusLead}
                onCancel={() => setStatusLead(null)}
                className="admin-modal"
                rootClassName="admin-modal"
                title="Durumu Güncelle"
                onOk={saveStatus}
                confirmLoading={statusSaving}
                okText="Kaydet"
                cancelText="İptal"
            >
                <Select
                    value={statusValue}
                    onChange={setStatusValue}
                    style={{ width: '100%', marginBottom: 12 }}
                    options={Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label }))}
                />
                <TextArea
                    rows={4}
                    value={noteValue}
                    onChange={(e) => setNoteValue(e.target.value)}
                    placeholder="Görüşme notu (opsiyonel)"
                    maxLength={2000}
                    showCount
                />
            </Modal>
        </motion.div>
    );
};

export default LeadsPage;
